/**
 * Renderer de tickets térmicos para el camino UNIVERSAL (window.print): produce HTML
 * con CSS `@page` para 58mm y 80mm, imprimible por AirPrint (iPad), el servicio de
 * impresión de Android, o cualquier impresora en PC — CON diálogo, pero funciona en TODO.
 *
 * Es el RESPALDO universal (backend 'browser') y también lo que se valida hoy desde una
 * tablet sin hardware silencioso. El ancho manda: 58mm ≈ 32 col, 80mm ≈ 48 col. El
 * `@page { size: Wmm auto; margin: 0 }` hace que el papel salga del ancho correcto sin
 * márgenes fantasma; el cuerpo usa `Wmm` con padding mínimo para no cortar contra el borde.
 */

export type PaperWidth = 58 | 80;

const mxn = (v: unknown) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const esc = (s: unknown) =>
  String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Métricas por ancho: tamaños de fuente y del código de entrega (px). */
function metrics(width: PaperWidth) {
  return width === 58
    ? { base: 11, h1: 14, big: 16, code: 34, pad: '2mm 2mm' }
    : { base: 12.5, h1: 16, big: 19, code: 46, pad: '2.5mm 3mm' };
}

/** Envuelve el cuerpo en un documento imprimible con `@page` del ancho elegido.
 *  `autoPrint=false` para el PREVIEW en pantalla (no dispara window.print). */
export function ticketDocument(width: PaperWidth, title: string, bodyHtml: string, autoPrint = true): string {
  const m = metrics(width);
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  @page { size: ${width}mm auto; margin: 0; }
  @media print { html, body { margin: 0 !important; } .noprint { display: none !important; } }
  * { box-sizing: border-box; }
  html { width: ${width}mm; margin: 0; padding: 0; }
  body {
    width: ${width}mm; min-width: ${width}mm; max-width: ${width}mm;
    margin: 0; padding: ${m.pad}; color: #000; background: #fff;
    font-family: ui-monospace, "Courier New", Consolas, monospace;
    font-size: ${m.base}px; line-height: 1.28; -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .logo { display: block; max-width: 100%; margin: 0 auto 4px; image-rendering: pixelated; }
  .hdr { margin: 2px 0 4px; font-size: ${m.base}px; }
  .inv { margin-top: 6px; }
  .inv .qr { width: ${width === 58 ? 30 : 40}mm; height: ${width === 58 ? 30 : 40}mm; display: block; margin: 4px auto; image-rendering: pixelated; }
  .mono { font-family: ui-monospace, "Courier New", monospace; }
  .sm { font-size: ${m.base - 2}px; word-break: break-all; }
  .c { text-align: center; } .r { text-align: right; } .b { font-weight: 700; }
  h1 { font-size: ${m.h1}px; text-align: center; margin: 0; font-weight: 700; letter-spacing: .02em; }
  .sub { text-align: center; font-size: ${m.base - 2}px; letter-spacing: .18em; text-transform: uppercase; color: #333; }
  .hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; gap: 8px; padding: 1px 0; }
  .row .lbl { flex: 1; } .row .val { white-space: nowrap; }
  .big { font-size: ${m.big}px; font-weight: 700; }
  .it { font-weight: 700; margin-top: 3px; }
  .mods { padding-left: 10px; font-size: ${m.base - 1.5}px; }
  .foot { text-align: center; font-size: ${m.base - 2}px; color: #000; margin-top: 8px; }
  /* Código de entrega: DOMINA visualmente (legible a un brazo de distancia). */
  .code-box { margin: 6px 0; padding: 4px 2px; border: 2px solid #000; border-radius: 6px; text-align: center; }
  .code-box .k { font-size: ${m.base - 2}px; text-transform: uppercase; letter-spacing: .12em; }
  .code-box .v { font-size: ${m.code}px; font-weight: 800; letter-spacing: .12em; line-height: 1.05; }
  .code-box .ref { font-size: ${m.base - 1}px; margin-top: 2px; }
</style></head><body>${bodyHtml}
${autoPrint ? `<script>window.onload=function(){setTimeout(function(){window.print();},60);};window.onafterprint=function(){setTimeout(function(){window.close();},200);};</script>` : ''}
</body></html>`;
}

const rowHtml = (lbl: string, val: string, cls = '') =>
  `<div class="row ${cls}"><span class="lbl">${esc(lbl)}</span><span class="val">${esc(val)}</span></div>`;

const codeBox = (code?: string | null, ref?: string | null, statusLabel?: string | null) =>
  code
    ? `<div class="code-box"><div class="k">Pedido para recoger</div><div class="v">${esc(code)}</div>` +
      (ref ? `<div class="ref">Ref: ${esc(ref)}</div>` : '') +
      (statusLabel ? `<div class="ref b">${esc(statusLabel)}</div>` : '') +
      `</div>`
    : '';

// ---------------------------------------------------------------------------
// Ticket de VENTA (recibo del cliente).
// ---------------------------------------------------------------------------
export interface SalesTicketItem { qty: number; description: string; lineTotal: number; mods?: string[] }
/** Bloque "Factura esta compra" para el HTML (QR como imagen data-URL). */
export interface TicketInvoiceHtml {
  code: string; qrDataUrl: string; urlLabel?: string;
  issuerRazon?: string | null; issuerRfc?: string | null;
  windowLabel?: string; globalNote?: string | null;
}
/** Personalización (V147) para el HTML: logo mono (data-URL), textos, QR configurable. */
export interface TicketBrandingHtml {
  logoDataUrl?: string | null;
  header?: string | null;
  footer?: string | null;
  customQr?: { qrDataUrl: string; label: string; sizeMm?: number } | null;
}
export interface SalesTicketData {
  brandName: string;
  at?: Date;
  delivery?: { code?: string | null; ref?: string | null; statusLabel?: string | null } | null;
  items: SalesTicketItem[];
  subtotal: number; taxTotal: number; total: number; tip: number; grandTotal: number;
  methodLabel: string;
  cash?: { received: number; change: number } | null;
  invoice?: TicketInvoiceHtml | null;
  branding?: TicketBrandingHtml | null;
  qrSizeMm?: number;
  footer?: string;
  /** 🅐 pieza 1 — PRE-CUENTA (sin cobrar): encabezado propio, SIN "TOTAL COBRADO",
   *  SIN forma de pago, SIN propina/cambio (el caller omite el QR de factura). */
  preCuenta?: boolean;
  /** Fix 11/09 — el tenant emite CFDI: "solicita tu factura al pagar" SIN QR. */
  offersCfdi?: boolean;
  /** V205 (Fidelidad, D2a) — código + avance en el ticket sellado. */
  loyalty?: { line: string; rewardReady?: boolean } | null;
  /** V205 — TICKET DE PROMOCIÓN (cortesía $0): sin cobro ni bloque de factura. */
  promo?: boolean;
}

const logoImg = (b?: TicketBrandingHtml | null) =>
  b?.logoDataUrl ? `<img class="logo" src="${b.logoDataUrl}" alt="logo" />` : '';
const headerHtml = (b?: TicketBrandingHtml | null) =>
  b?.header ? `<div class="c hdr">${esc(b.header).replace(/\n/g, '<br>')}</div>` : '';
const customQrHtml = (b?: TicketBrandingHtml | null) =>
  b?.customQr?.qrDataUrl
    ? `<hr class="hr"><div class="inv c"><img class="qr" src="${b.customQr.qrDataUrl}" alt="QR"${qrStyle(b.customQr.sizeMm)} />` +
      (b.customQr.label ? `<div class="b">${esc(b.customQr.label)}</div>` : '') + `</div>`
    : '';

const qrStyle = (mm?: number) => (mm ? ` style="width:${mm}mm;height:${mm}mm"` : '');

function invoiceHtml(inv: TicketInvoiceHtml, sizeMm?: number): string {
  return `<hr class="hr"><div class="inv c">` +
    `<div class="b">FACTURA ESTA COMPRA</div>` +
    `<img class="qr" src="${inv.qrDataUrl}" alt="QR autofactura"${qrStyle(sizeMm)} />` +
    `<div class="mono">Ticket: ${esc(inv.code)}</div>` +
    (inv.urlLabel ? `<div class="mono sm">${esc(inv.urlLabel)}</div>` : '') +
    (inv.globalNote ? `<div class="sm b">${esc(inv.globalNote)}</div>` : '') +
    ((inv.issuerRazon || inv.issuerRfc) ? `<div class="sm">Emisor: ${esc(inv.issuerRazon ?? '')} · RFC ${esc(inv.issuerRfc ?? '')}</div>` : '') +
    (inv.windowLabel ? `<div class="sm">${esc(inv.windowLabel)}</div>` : '') +
    `</div>`;
}

export function salesTicketHtml(d: SalesTicketData, width: PaperWidth, autoPrint = true): string {
  const at = d.at ?? new Date();
  const items = d.items.map((it) =>
    `<div class="it"><div class="row"><span class="lbl"><span class="b">${Number(it.qty)}×</span> ${esc(it.description)}</span>` +
    `<span class="val">${mxn(it.lineTotal)}</span></div>` +
    (it.mods?.length ? `<div class="mods">${it.mods.map((m) => esc(m)).join('<br>')}</div>` : '') +
    `</div>`).join('');

  const cash = d.cash
    ? rowHtml('Recibido', mxn(d.cash.received)) + rowHtml('Cambio', mxn(d.cash.change), 'b')
    : '';

  // 🅐 pieza 1 — bloque de pago: en pre-cuenta se sustituye por la leyenda de pendiente
  // (sin propina, sin "TOTAL COBRADO", sin cambio, sin forma de pago; QR ya viene null).
  const payBlock = d.preCuenta
    ? `<hr class="hr"><div class="c b">CUENTA — NO ES COMPROBANTE FISCAL</div>` +
      (d.offersCfdi ? `<div class="c sm">Solicita tu factura al pagar</div>` : '') +
      `<p class="foot">${esc(d.branding?.footer ?? d.footer ?? '¡Gracias por tu visita!')}</p>`
    : d.promo
    ? `<hr class="hr"><div class="c b">CORTESÍA — SIN COBRO</div>` +
      `<p class="foot">${esc(d.branding?.footer ?? d.footer ?? '¡Gracias por tu visita!')}</p>`
    : rowHtml('Propina', mxn(d.tip)) +
      `<hr class="hr">` +
      `<div class="row big"><span class="lbl">TOTAL COBRADO</span><span class="val">${mxn(d.grandTotal)}</span></div>` +
      (cash ? `<hr class="hr">${cash}` : '') +
      `<p class="foot">Pago: ${esc(d.methodLabel)}<br>${esc(d.branding?.footer ?? d.footer ?? '¡Gracias por tu visita!')}</p>`;

  const title = d.preCuenta ? 'PRE-CUENTA · pendiente de pago'
    : d.promo ? 'TICKET DE PROMOCIÓN · cortesía de fidelidad' : 'Recibo de venta';
  // V205 (D2a) — código + avance del programa de lealtad (no en pre-cuenta).
  const loyaltyHtml = d.loyalty?.line && !d.preCuenta
    ? `<hr class="hr"><div class="c b">${esc(d.loyalty.line)}</div>` +
      (d.loyalty.rewardReady ? `<div class="c sm">Ya tienes tu recompensa: pídela en caja.</div>` : '')
    : '';
  const body =
    logoImg(d.branding) +
    `<h1>${esc(d.brandName)}</h1><div class="sub">${esc(title)}</div>` +
    headerHtml(d.branding) +
    codeBox(d.delivery?.code, d.delivery?.ref, d.delivery?.statusLabel) +
    `<hr class="hr">${items}<hr class="hr">` +
    rowHtml('Subtotal', mxn(d.subtotal)) +
    rowHtml('IVA (16%)', mxn(d.taxTotal)) +
    rowHtml('Total', mxn(d.total), 'b') +
    payBlock +
    loyaltyHtml +
    (d.invoice && !d.promo ? invoiceHtml(d.invoice, d.qrSizeMm) : '') +
    customQrHtml(d.branding) +
    `<p class="foot">${esc(at.toLocaleString('es-MX'))}</p>`;
  return ticketDocument(width, title, body, autoPrint);
}

// ---------------------------------------------------------------------------
// COMANDA (cocina) — sin precios.
// ---------------------------------------------------------------------------
export interface ComandaItem { qty: number; description: string; notes?: string | null; modifiers?: { name: string }[] }
export interface ComandaData { brandName: string; tableLabel: string; items: ComandaItem[]; at?: Date }

export function comandaHtml(d: ComandaData, width: PaperWidth): string {
  const at = d.at ?? new Date();
  const items = d.items.map((it) => {
    const mods = [
      ...(it.notes ? [`&gt;&gt; ${esc(it.notes)}`] : []),
      ...(it.modifiers ?? []).map((m) => `+ ${esc(m.name)}`),
    ];
    return `<div class="it"><span class="b">${Number(it.qty)}× ${esc(it.description)}</span>` +
      (mods.length ? `<div class="mods">${mods.join('<br>')}</div>` : '') + `</div>`;
  }).join('');
  const body =
    `<h1>COCINA</h1><div class="sub">${esc(d.brandName)}</div>` +
    `<div class="c big" style="margin:6px 0">${esc(d.tableLabel)}</div><hr class="hr">` +
    items + `<hr class="hr"><p class="c">Enviado ${esc(at.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false }))}</p>`;
  return ticketDocument(width, 'Comanda', body);
}

// ---------------------------------------------------------------------------
// CORTE de caja.
// ---------------------------------------------------------------------------
// PINK #2 PR C — crédito/débito separados; 'CARD' legacy = sin especificar.
const METHOD_LABEL: Record<string, string> = {
  CASH: 'Efectivo', CARD_CREDIT: 'Tarjeta crédito', CARD_DEBIT: 'Tarjeta débito',
  CARD: 'Tarjeta (sin especificar)', TRANSFER: 'Transferencia', ONLINE: 'En línea',
};
export interface CorteData {
  brandName: string; dateLabel: string; shiftLabel: string;
  byMethod: { method: string; sales: number; tips: number; orders: number; collected: number }[];
  totals: { subtotal: number; taxTotal: number; sales: number; tips: number; collected: number; orders: number;
            roundingDiff?: number; cashCollected?: number; expectedCash?: number; cashTips?: number };
  at?: Date;
}

export function corteHtml(d: CorteData, width: PaperWidth): string {
  const at = d.at ?? new Date();
  const methods = d.byMethod.filter((r) => r.orders || r.collected).map((r) =>
    `<div class="b">${esc(METHOD_LABEL[r.method] ?? r.method)}</div>` +
    rowHtml('  Ventas', mxn(r.sales)) + rowHtml('  Propinas', mxn(r.tips)) +
    rowHtml(`  Órdenes (${Number(r.orders)})`, mxn(r.collected))).join('');
  const arqueo = Number(d.totals.roundingDiff) > 0
    ? `<hr class="hr">` + rowHtml('Efectivo cobrado', mxn(d.totals.cashCollected)) +
      rowHtml('Diferencial redondeo', `-${mxn(d.totals.roundingDiff)}`) +
      `<div class="row big"><span class="lbl">EFECTIVO EN CAJA</span><span class="val">${mxn(d.totals.expectedCash)}</span></div>`
    : '';
  const body =
    `<h1>CORTE DE CAJA</h1><div class="sub">${esc(d.brandName)}</div>` +
    `<p class="c">${esc(d.dateLabel)}<br>${esc(d.shiftLabel)}</p><hr class="hr">` +
    methods + `<hr class="hr">` +
    rowHtml('Subtotal', mxn(d.totals.subtotal)) + rowHtml('IVA', mxn(d.totals.taxTotal)) +
    rowHtml('Ventas (c/IVA)', mxn(d.totals.sales)) + rowHtml('Propinas', mxn(d.totals.tips)) +
    (Number(d.totals.cashTips) > 0 ? rowHtml('  de eso en efectivo', mxn(d.totals.cashTips)) : '') +
    rowHtml('# Órdenes', String(Number(d.totals.orders))) + `<hr class="hr">` +
    `<div class="row big"><span class="lbl">TOTAL</span><span class="val">${mxn(d.totals.collected)}</span></div>` +
    arqueo + `<p class="foot">Generado ${esc(at.toLocaleString('es-MX'))}</p>`;
  return ticketDocument(width, 'Corte de caja', body);
}

// ---------------------------------------------------------------------------
// Comprobante de CANCELACIÓN.
// ---------------------------------------------------------------------------
export interface CancelData {
  brandName: string; folio: string; at?: Date; cancelledBy?: string | null; reason?: string | null;
  items: { qty: number; description: string }[];
  branding?: TicketBrandingHtml | null;
}
export function cancelHtml(d: CancelData, width: PaperWidth): string {
  const at = d.at ?? new Date();
  const items = d.items.map((it) =>
    `<div class="row"><span class="lbl" style="text-decoration:line-through">${Number(it.qty)}× ${esc(it.description)}</span></div>`).join('');
  const body =
    logoImg(d.branding) +
    `<h1>CANCELACIÓN</h1><div class="sub">${esc(d.brandName)}</div>` +
    headerHtml(d.branding) +
    `<p class="c b">Folio ${esc(d.folio)}</p><hr class="hr">` + items + `<hr class="hr">` +
    (d.cancelledBy ? rowHtml('Canceló', d.cancelledBy) : '') +
    (d.reason ? `<p>Motivo: ${esc(d.reason)}</p>` : '') +
    (d.branding?.footer ? `<p class="foot">${esc(d.branding.footer).replace(/\n/g, '<br>')}</p>` : '') +
    `<p class="foot">${esc(at.toLocaleString('es-MX'))}<br>Comprobante sin valor fiscal</p>`;
  return ticketDocument(width, 'Cancelación', body);
}
